import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactElement } from 'react';
import { cn } from './utils';

/**
 * Der Knopf aus shadcn/ui. Übersetzung und Auslassungen: siehe `utils.ts`.
 *
 * **`size` ist hier nicht schmückend, sondern die Dichte.** `default` ist
 * 36 px hoch — das ist die Höhe, die eine Anmeldemaske will. `sm` ist 32 px,
 * und das ist die Höhe, die eine Oberfläche will, in der zwanzig Knöpfe
 * gleichzeitig auf dem Bildschirm stehen. `ui/elements.tsx` wählt deshalb
 * `sm` als Vorgabe und nicht `default`.
 */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium ' +
    'whitespace-nowrap transition-colors outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // `shadow-elev` statt der Vorlagen-`shadow-sm` — Endnutzerinnen-
        // Feedback, `W-designpass`, 2026-08-31 („weichere Flächen"). Kein neuer
        // Token: `--fa-elev` (`tailwind.config.js`) trug die Zeile „Hauptknopf
        // und Detailtafel" schon seit der Design-Annäherung vom 2026-08-24 im
        // eigenen Kopfkommentar, wurde aber nirgends verdrahtet — dieser
        // Knopf ist die eine Stelle, die Karten (`ui/shadcn/card.tsx`) bleiben
        // bewusst flach (dort dokumentierte Regel: „nur zwei Stufen, flach als
        // Standard für Karten"), das gilt unverändert und wird hier nicht
        // angetastet.
        default: 'bg-primary text-primary-foreground shadow-elev hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        // `bg-card` und nicht `bg-background` wie in der Vorlage: Ein Knopf
        // steht in dieser Anwendung fast immer auf einer Karte. Auf
        // `bg-background` wäre er im Hellen grau auf weiss und damit eine
        // Vertiefung statt einer Erhebung.
        outline:
          'border border-input bg-card shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

/*
 * Der Fokusring ist gegenüber der Vorlage GETAUSCHT, und zwar zurück auf das,
 * was diese Anwendung schon hatte: `ring-2 … ring-offset-2` statt des
 * `ring-[3px] ring-ring/50` von shadcn. Der halbdurchsichtige Ring der Vorlage
 * verschwimmt auf `--accent` (die überfahrene Tabellenzeile) mit seinem
 * Untergrund; der abgesetzte Ring hat einen Spalt in der Farbe der Seite und
 * bleibt damit auf jedem Untergrund derselbe Ring. Wer acht Stunden mit der
 * Tastatur bucht, sieht ihn hundertmal am Tag.
 */

type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>;

function Button({ className, variant, size, ...props }: ButtonProps): ReactElement {
  return (
    <button
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
